import moment from "moment";
import { GetFileSize, UserHasAcl } from "../../../Utils/helpers";
import ApiClient from "../api-client";
import { apiBaseUrls, cacheService, EnvType } from "../services";
import { AclTypes } from "../Types/acl-types";
import { AddFileUserDataRequest } from "../Types/add-file-user-data-request";
import { AsperaUploadMediaFilesDto, AssetMediaCardDto, DbFileInfos } from "../Types/db-file-infos";
import { GenericResponseStd } from "../Types/generic-response-std";
import { GetFilesResponse } from "../Types/get-files-response";
import { GetMediaResourceResponse } from "../Types/get-media-resource-response";
import { GetNleTimelineInfos } from "../Types/get-nle-timeline-infos";
import { GetSmilCustomRequest } from "../Types/get-smil-custom-request";
import { GetStreamingSmilByObjectIdResponse } from "../Types/get-streaming-smil-by-object-id-response";
import { GetSubtitleParsedInfosResponse } from "../Types/get-subtitle-parsed-infos-response";
import { GetSubtitlesCheckerReportResponse } from "../Types/get-subtitles-checker-report-response";
import { GetSubtitlesCheckerTemplatesResponse } from "../Types/get-subtitles-checker-templates-response";
import { GetSubtitlesResponse } from "../Types/get-subtitles-response";
import { RegisterAndParseFileRequest } from "../Types/register-and-parse-file-request";
import { StoragePathAliasType } from "../Types/storage-path-alias-type";
import { SyncMediaFileResponse } from "../Types/sync-media-file-response";
import { UploadMediaInfoReponse } from "../Types/upload-media-info-reponse";
import { UserDataCustomType } from "../Types/user-data-custom-type";
import { UserDataStatus } from "../Types/user-data-status";
import { UserDataType } from "../Types/user-data-type";
import { default as IMediaApiClient, default as MediaApiClient } from "./media-api-client";
import GlobalConst from "../../Utils/Constants/globals";
import { FileCategory } from "../Types/file-category";

export default class MediaService {
  mediaApiClient: IMediaApiClient;

  constructor(envType: EnvType) {
    this.mediaApiClient = new MediaApiClient(new ApiClient(envType), envType);
  }

  async GetMediaFilesForAsperaUploadModal(assetId: number): Promise<AsperaUploadMediaFilesDto[]> {
    const response = await this.mediaApiClient.GetFilesByObjectId(assetId);

    if (!response.files) return [];

    return response.files.map((file) => ({
      id: file.id!,
      formatId: file.formatId!,
      extension: file.extension!,
      name: file.name!,
    }));
  }

  async GetMediaFilesOfAssetForBundlemaker(assetId: number): Promise<any[]> {
    return (await this.mediaApiClient.GetFilesByObjectId(assetId)).files || [];
  }

  async GetMediaFilesOfAssetForAssetPanel(assetId: number): Promise<AssetMediaCardDto[]> {
    const response = await this.mediaApiClient.GetFilesByObjectId(assetId);

    if (!response.files) return [];

    return response.files.map((file) => ({
      fileId: file.id,
      isDownloable: this.IsMediaDownloadable(file),
      downloadLink: this.GetMediaDownloadLink(assetId, file),
      title: file.name || "-",
      status: file.status!,
      duration: (file.properties.duration?.timecode as string) || "-",
      storageLocation: file.storageInfos?.name || "-",
      category: file.category?.toString() || "-",
      accessibility: file.accessibility?.toString() || "-",
      size: GetFileSize(file.properties!.fileInfos!.sizeInBytes!) || "-",
      date: moment(file.creationInfos?.date).format(GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS) || "-",
    }));
  }

  async UploadMediaFile(
    objectId: number,
    filesToUpload: File[],
    formatId: number,
    dataType?: UserDataType,
    dataStatus?: UserDataStatus,
    dataCustomType?: UserDataCustomType,
    abortSignal?: AbortSignal,
    onUploadProgressCallback?: (progressEvent: any) => void
  ): Promise<UploadMediaInfoReponse> {
    const formData = new FormData();
    formData.append("objectId", String(objectId));
    formData.append("formatId", String(formatId));
    formData.append("file", filesToUpload[0]);

    if (dataType) formData.append("userDataType", dataType);
    if (dataStatus) formData.append("userDataStatus", dataStatus);
    if (dataCustomType) formData.append("userDataCustomType", dataCustomType);

    return this.mediaApiClient.UploadMediaFile(formData, abortSignal, onUploadProgressCallback);
  }

  //------PRIVATE------
  private IsMediaDownloadable(file: DbFileInfos): boolean {
    if (file.formatInfos === null || file.formatInfos.isDownloadable === null) return false;

    return (
      file.formatInfos.isDownloadable &&
      UserHasAcl(AclTypes.aclMediaFileDownload) &&
      ((file.formatInfos.isRestricted !== null && file.formatInfos.isRestricted) ||
        UserHasAcl(AclTypes.aclRestrictedFileDownload))
    );
  }

  private GetMediaDownloadLink(assetId: number, file: DbFileInfos): string {
    return this.GetServiceUrl() + `/media-infos/object/${assetId}/${file.id}/${cacheService.GetAuthKey()}`;
  }

  //------USELESS------ => Keep it for calls signatures

  async GetFilesByObjectId(objectId: number): Promise<GetFilesResponse> {
    return await this.mediaApiClient.GetFilesByObjectId(objectId);
  }
  async GetChildrenFilesByObjectId(objectId: number): Promise<GetFilesResponse> {
    return await this.mediaApiClient.GetChildrenFilesByObjectId(objectId);
  }
  async RegisterAndParseFile(request: RegisterAndParseFileRequest): Promise<GenericResponseStd> {
    return await this.mediaApiClient.RegisterAndParseFile(request);
  }
  DownloadMediaFile(objectId: number, fileId: number, authKey: string): string {
    return this.mediaApiClient.DownloadMediaFile(objectId, fileId, authKey);
  }
  DownloadMediaFileByCategory(objectId: number, category: FileCategory, authKey: string): string {
    return this.mediaApiClient.DownloadMediaFileByCategory(objectId, category, authKey);
  }
  DownloadShareMediaFile(mediaKey: string): string {
    return this.mediaApiClient.DownloadShareMediaFile(mediaKey);
  }
  async DeleteMediaFile(fileId: number): Promise<GenericResponseStd> {
    return await this.mediaApiClient.DeleteMediaFile(fileId);
  }
  async SyncMediaFile(fileId: number): Promise<SyncMediaFileResponse> {
    return await this.mediaApiClient.SyncMediaFile(fileId);
  }
  async SyncMediaFileMetadata(fileId: number): Promise<SyncMediaFileResponse> {
    return await this.mediaApiClient.SyncMediaFileMetadata(fileId);
  }
  async ParseMediaFile(fileId: number): Promise<SyncMediaFileResponse> {
    return await this.mediaApiClient.ParseMediaFile(fileId);
  }
  async AddUserDataOnMediaFile(request: AddFileUserDataRequest): Promise<GenericResponseStd> {
    return await this.mediaApiClient.AddUserDataOnMediaFile(request);
  }
  async GetStreamingSmilByObjectId(
    objectId: number,
    episodeNumber?: number | null
  ): Promise<GetStreamingSmilByObjectIdResponse> {
    if (episodeNumber !== undefined && episodeNumber !== null && episodeNumber > 0)
      return await this.mediaApiClient.GetStreamingSmilByObjectIdFilteredByEpisodeNumber(objectId, episodeNumber);
    else return await this.mediaApiClient.GetStreamingSmilByObjectId(objectId);
  }
  async GetStreamingSmilWithCustoms(request: GetSmilCustomRequest): Promise<GetStreamingSmilByObjectIdResponse> {
    return await this.mediaApiClient.GetStreamingSmilWithCustoms(request);
  }
  async GetStreamingSmilByShareMediaKey(mediaKey: string): Promise<GetStreamingSmilByObjectIdResponse> {
    return await this.mediaApiClient.GetStreamingSmilByShareMediaKey(mediaKey);
  }
  async GetMediaResources(): Promise<GetMediaResourceResponse> {
    return await this.mediaApiClient.GetMediaResources();
  }
  async GetSubtitleParsedInfosByObjectId(objectId: number): Promise<GetSubtitleParsedInfosResponse> {
    return await this.mediaApiClient.GetSubtitleParsedInfosByObjectId(objectId);
  }
  GetWebVTTSubtitle(objectId: number, cultureId: number): string {
    return this.mediaApiClient.GetWebVTTSubtitle(objectId, cultureId);
  }
  async GetSubtitleFileAsMetadata(fileId: number): Promise<GetSubtitlesResponse> {
    return await this.mediaApiClient.GetSubtitleFileAsMetadata(fileId);
  }
  async GetSubtitleCheckTemplates(): Promise<GetSubtitlesCheckerTemplatesResponse> {
    return await this.mediaApiClient.GetSubtitleCheckTemplates();
  }
  async GetSubtitleCheckReport(fileId: number, templateId: number): Promise<GetSubtitlesCheckerReportResponse> {
    return await this.mediaApiClient.GetSubtitleCheckReport(fileId, templateId);
  }
  GetImagePreviewer(objectId: number, authKey: string): string {
    return this.mediaApiClient.GetImagePreviewer(objectId, authKey);
  }
  GetFileImagePreviewer(fileId: number, authKey: string): string {
    return this.mediaApiClient.GetFileImagePreviewer(fileId, authKey);
  }
  GetShareImagePreviewer(mediaKey: string): string {
    return this.mediaApiClient.GetShareImagePreviewer(mediaKey);
  }
  GetScreenshotFromAssetVideoProxy(objectId: number, frame: number, authKey: string) {
    window.open(this.mediaApiClient.GetScreenshotFromAssetVideoProxy(objectId, frame, authKey));
  }
  GetScreenshotFromAssetVideoHires(objectId: number, frame: number, authKey: string) {
    window.open(this.mediaApiClient.GetScreenshotFromAssetVideoHires(objectId, frame, authKey));
  }
  async GetNleTimeline(objectId: number): Promise<GetNleTimelineInfos> {
    return await this.mediaApiClient.GetNleTimeline(objectId);
  }
  async GetNleTimelineWithAlias(objectId: number, pathAlias: StoragePathAliasType): Promise<GetNleTimelineInfos> {
    return await this.mediaApiClient.GetNleTimelineWithAlias(objectId, pathAlias);
  }
  async SetPlayLog(objectId: number): Promise<GenericResponseStd> {
    return await this.mediaApiClient.SetPlayLog(objectId);
  }

  //------Utils------
  GetServiceUrl(): string {
    return this.mediaApiClient.GetApiUrl();
  }
}
